import React, { useState, useContext } from "react";
import { AuthContext } from "@/App";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Card, CardContent } from "@/components/ui/card";
import { Building2, Users, TrendingUp, CheckCircle2, ArrowRight, Home, Handshake, BarChart3, FileText, Target, Briefcase, MapPin, Zap, Star } from "lucide-react";
import axios from "axios";
import { toast } from "sonner";
import Header from "@/components/Header";

// Use environment variable with fallback to current origin
const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || window.location.origin;
const API = `${BACKEND_URL}/api`;

export default function LandingPage() {
  const { user } = useContext(AuthContext);
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    phone: "",
    property_address: "",
    city: "",
    postal_code: "",
    property_type: "",
    num_units: "1",
    management_status: ""
  });
  const [submitting, setSubmitting] = useState(false);
  const [locationDetected, setLocationDetected] = useState(false);

  // Detect user's location on component mount
  React.useEffect(() => {
    const detectLocation = async () => {
      if (navigator.geolocation) {
        navigator.geolocation.getCurrentPosition(
          async (position) => {
            const { latitude, longitude } = position.coords;
            
            try {
              // Use reverse geocoding to get city
              const response = await fetch(
                `https://nominatim.openstreetmap.org/reverse?format=json&lat=${latitude}&lon=${longitude}&addressdetails=1`
              );
              const data = await response.json();
              
              if (data.address) {
                const city = data.address.city || data.address.town || data.address.municipality || "";
                const postalCode = data.address.postcode || "";
                
                setFormData(prev => ({
                  ...prev,
                  city: city,
                  postal_code: postalCode
                }));
                setLocationDetected(true);
              }
            } catch (error) {
              console.log("Could not detect location:", error);
            }
          },
          (error) => {
            console.log("Geolocation not available:", error);
          }
        );
      }
    };
    
    detectLocation();
  }, []);

  React.useEffect(() => {
    if (user) {
      navigate("/crm");
    }
  }, [user, navigate]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);

    try {
      const response = await axios.post(`${API}/leads`, {
        ...formData,
        num_units: parseInt(formData.num_units),
        disclaimer_accepted: true
      });

      // Redirect to results page
      navigate(`/results/${response.data.id}`);
    } catch (error) {
      console.error("Error submitting lead:", error);
      toast.error("Something went wrong. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  const handleCRMLogin = () => {
    // Redirect to Emergent Auth - it will return to root with session_id in hash
    const redirectUrl = encodeURIComponent(window.location.origin);
    window.location.href = `https://auth.emergentagent.com/?redirect=${redirectUrl}`;
  };

  return (
    <div className="min-h-screen bg-white">
      {/* Header with Full Navigation */}
      <Header variant="full" />

      {/* Hero Section */}
      <section className="container mx-auto px-4 sm:px-6 py-12 sm:py-20">
        <div className="grid lg:grid-cols-2 gap-8 sm:gap-12 items-center">
          <div className="space-y-6 sm:space-y-8">
            <h1 className="text-3xl sm:text-4xl lg:text-5xl xl:text-6xl font-bold leading-tight" style={{ color: '#1E1E1E' }}>
              Where Property Owners Meet Property Managers
            </h1>
            <p className="text-base sm:text-lg space-y-2" style={{ color: '#3A3A3A' }}>
              <span className="block">Owners find trusted management.</span>
              <span className="block">Managers find quality leads.</span>
              <span className="block font-semibold" style={{ color: '#3b82f6' }}>Everyone wins with TopProp.</span>
            </p>
            
            {/* Dual CTA Buttons */}
            <div className="space-y-4">
              <div className="flex flex-col sm:flex-row gap-4">
                {/* Primary Button - Solid Red */}
                <Button
                  onClick={() => navigate("/get-matched")}
                  size="lg"
                  className="text-white px-8 py-6 text-lg font-semibold w-full sm:w-auto transition-all duration-300"
                  style={{ 
                    backgroundColor: '#3b82f6',
                    borderRadius: '12px',
                    boxShadow: '0 8px 20px rgba(59, 130, 246, 0.3)'
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.background = 'linear-gradient(90deg, #3b82f6 0%, #2563eb 100%)';
                    e.currentTarget.style.color = '#000000';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.backgroundColor = '#3b82f6';
                    e.currentTarget.style.color = '#FFFFFF';
                  }}
                  title="For property owners or investors looking for a reliable management company"
                >
                  I Need Management
                </Button>
                
                {/* Secondary Button - Red Outline */}
                <Button
                  onClick={() => navigate("/company-signup")}
                  size="lg"
                  variant="outline"
                  className="bg-white px-8 py-6 text-lg font-semibold w-full sm:w-auto transition-all duration-300"
                  style={{ 
                    color: '#3b82f6',
                    borderColor: '#3b82f6',
                    borderWidth: '2px',
                    borderRadius: '12px',
                    boxShadow: '0 4px 12px rgba(0, 0, 0, 0.08)'
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.backgroundColor = '#3b82f6';
                    e.currentTarget.style.color = '#FFFFFF';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.backgroundColor = '#FFFFFF';
                    e.currentTarget.style.color = '#3b82f6';
                  }}
                  title="For property management companies ready to receive qualified local leads"
                >
                  I Offer Management
                </Button>
              </div>
              
              {/* Supporting Tagline */}
              <p className="text-sm italic" style={{ color: '#9C9C9C' }}>
                Instantly connect with the right property management partner in your area.
              </p>
            </div>
          </div>

          <div className="grid grid-cols-2 gap-6">
            <Card className="bg-white transition-all duration-300 transform hover:-translate-y-1" style={{ border: '1px solid #F7F7F7', boxShadow: '0 4px 12px rgba(0, 0, 0, 0.08)', borderRadius: '12px' }}>
              <CardContent className="p-6 space-y-2">
                <div className="h-12 w-12 rounded-lg flex items-center justify-center" style={{ backgroundColor: 'rgba(59, 130, 246, 0.1)' }}>
                  <Building2 className="h-6 w-6" style={{ color: '#3b82f6' }} />
                </div>
                <h3 className="text-2xl font-bold" style={{ color: '#1E1E1E' }}>10,000+</h3>
                <p style={{ color: '#3A3A3A' }}>Units Introduced to Clients</p>
              </CardContent>
            </Card>

            <Card className="bg-white transition-all duration-300 transform hover:-translate-y-1 mt-8" style={{ border: '1px solid #F7F7F7', boxShadow: '0 4px 12px rgba(0, 0, 0, 0.08)', borderRadius: '12px' }}>
              <CardContent className="p-6 space-y-2">
                <div className="h-12 w-12 rounded-lg flex items-center justify-center" style={{ backgroundColor: 'rgba(244, 162, 97, 0.15)' }}>
                  <Users className="h-6 w-6" style={{ color: '#F4A261' }} />
                </div>
                <h3 className="text-2xl font-bold" style={{ color: '#1E1E1E' }}>1,200+</h3>
                <p style={{ color: '#3A3A3A' }}>Happy Clients</p>
              </CardContent>
            </Card>

            <Card className="bg-white transition-all duration-300 transform hover:-translate-y-1" style={{ border: '1px solid #F7F7F7', boxShadow: '0 4px 12px rgba(0, 0, 0, 0.08)', borderRadius: '12px' }}>
              <CardContent className="p-6 space-y-2">
                <div className="h-12 w-12 rounded-lg flex items-center justify-center" style={{ backgroundColor: 'rgba(244, 162, 97, 0.15)' }}>
                  <TrendingUp className="h-6 w-6" style={{ color: '#F4A261' }} />
                </div>
                <h3 className="text-2xl font-bold" style={{ color: '#1E1E1E' }}>95%</h3>
                <p style={{ color: '#3A3A3A' }}>Client Retention</p>
              </CardContent>
            </Card>

            <Card className="bg-white transition-all duration-300 transform hover:-translate-y-1 mt-8" style={{ border: '1px solid #F7F7F7', boxShadow: '0 4px 12px rgba(0, 0, 0, 0.08)', borderRadius: '12px' }}>
              <CardContent className="p-6 space-y-2">
                <div className="h-12 w-12 rounded-lg flex items-center justify-center" style={{ backgroundColor: 'rgba(59, 130, 246, 0.1)' }}>
                  <CheckCircle2 className="h-6 w-6" style={{ color: '#3b82f6' }} />
                </div>
                <h3 className="text-2xl font-bold" style={{ color: '#1E1E1E' }}>Canada Wide</h3>
                <p style={{ color: '#3A3A3A' }}>Coverage</p>
              </CardContent>
            </Card>

            <Card 
              onClick={() => navigate("/reviews")}
              className="bg-white transition-all duration-300 transform hover:-translate-y-1 cursor-pointer hover:shadow-xl" 
              style={{ border: '2px solid #3b82f6', boxShadow: '0 4px 12px rgba(59, 130, 246, 0.15)', borderRadius: '12px' }}
            >
              <CardContent className="p-6 space-y-2">
                <div className="flex gap-1 mb-2">
                  {[1, 2, 3, 4, 5].map((star) => (
                    <Star key={star} className="h-5 w-5 fill-current" style={{ color: '#fbbf24' }} />
                  ))}
                </div>
                <h3 className="text-2xl font-bold" style={{ color: '#3b82f6' }}>Reviews</h3>
                <p style={{ color: '#3A3A3A' }}>See What Clients Say</p>
              </CardContent>
            </Card>

            <Card 
              onClick={() => navigate("/blog")}
              className="bg-white transition-all duration-300 transform hover:-translate-y-1 cursor-pointer hover:shadow-xl mt-8" 
              style={{ border: '2px solid #3b82f6', boxShadow: '0 4px 12px rgba(59, 130, 246, 0.15)', borderRadius: '12px' }}
            >
              <CardContent className="p-6 space-y-2">
                <div className="h-12 w-12 rounded-lg flex items-center justify-center" style={{ backgroundColor: 'rgba(59, 130, 246, 0.1)' }}>
                  <FileText className="h-6 w-6" style={{ color: '#3b82f6' }} />
                </div>
                <h3 className="text-2xl font-bold" style={{ color: '#3b82f6' }}>Blog 📖</h3>
                <p style={{ color: '#3A3A3A' }}>Property Management Tips</p>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* How It Works Section */}
      <section className="py-20 bg-slate-50">
        <div className="container mx-auto px-4 sm:px-6">
          <div className="text-center mb-16">
            <h2 className="text-4xl sm:text-5xl font-bold text-slate-900 mb-4">
              How It Works
            </h2>
            <p className="text-xl text-slate-600 max-w-3xl mx-auto">
              Get matched with the perfect property management company in three simple steps
            </p>
          </div>

          <div className="grid lg:grid-cols-2 gap-8 lg:gap-12">
            {/* For Property Owners */}
            <div className="bg-white p-8 transition-all duration-300" style={{ border: '2px solid #3b82f6', borderRadius: '12px', boxShadow: '0 4px 12px rgba(0, 0, 0, 0.08)' }}>
              <div className="text-center mb-8">
                <div className="inline-block text-white px-6 py-3 font-bold text-lg mb-2" style={{ backgroundColor: '#3b82f6', borderRadius: '24px' }}>
                  For Property Owners
                </div>
              </div>
              
              <div className="space-y-8">
                {/* Step 1 */}
                <div className="flex gap-4">
                  <div className="flex-shrink-0">
                    <div className="h-14 w-14 rounded-full flex items-center justify-center" style={{ backgroundColor: 'rgba(59, 130, 246, 0.1)' }}>
                      <Home className="h-7 w-7" style={{ color: '#3b82f6' }} />
                    </div>
                  </div>
                  <div>
                    <h3 className="text-xl font-bold mb-2" style={{ color: '#1E1E1E' }}>Tell Us About Your Property</h3>
                    <p style={{ color: '#3A3A3A' }}>Share a few quick details — location, type, and what kind of help you're looking for.</p>
                  </div>
                </div>

                {/* Step 2 */}
                <div className="flex gap-4">
                  <div className="flex-shrink-0">
                    <div className="h-14 w-14 rounded-full flex items-center justify-center" style={{ backgroundColor: 'rgba(244, 162, 97, 0.15)' }}>
                      <Target className="h-7 w-7" style={{ color: '#F4A261' }} />
                    </div>
                  </div>
                  <div>
                    <h3 className="text-xl font-bold mb-2" style={{ color: '#1E1E1E' }}>Get Matched Instantly</h3>
                    <p style={{ color: '#3A3A3A' }}>We connect you with top-rated local property management companies that fit your needs.</p>
                  </div>
                </div>

                {/* Step 3 */}
                <div className="flex gap-4">
                  <div className="flex-shrink-0">
                    <div className="h-14 w-14 rounded-full flex items-center justify-center" style={{ backgroundColor: 'rgba(59, 130, 246, 0.1)' }}>
                      <CheckCircle2 className="h-7 w-7" style={{ color: '#3b82f6' }} />
                    </div>
                  </div>
                  <div>
                    <h3 className="text-xl font-bold mb-2" style={{ color: '#1E1E1E' }}>Choose with Confidence</h3>
                    <p style={{ color: '#3A3A3A' }}>Review your matches, compare services, and pick the manager that feels right for you.</p>
                  </div>
                </div>
              </div>

              <div className="mt-8 text-center">
                <Button
                  onClick={() => navigate("/get-matched")}
                  className="text-white px-8 py-4 text-base font-semibold transition-all duration-300"
                  style={{ 
                    backgroundColor: '#3b82f6',
                    borderRadius: '12px',
                    boxShadow: '0 8px 20px rgba(59, 130, 246, 0.3)'
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.background = 'linear-gradient(90deg, #3b82f6 0%, #2563eb 100%)';
                    e.currentTarget.style.color = '#000000';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.backgroundColor = '#3b82f6';
                    e.currentTarget.style.color = '#FFFFFF';
                  }}
                >
                  Get Started <ArrowRight className="ml-2 h-5 w-5" />
                </Button>
              </div>
            </div>

            {/* For Property Management Companies */}
            <div className="bg-white p-8 transition-all duration-300" style={{ border: '2px solid #3b82f6', borderRadius: '12px', boxShadow: '0 4px 12px rgba(0, 0, 0, 0.08)' }}>
              <div className="text-center mb-8">
                <div className="inline-block bg-white px-6 py-3 font-bold text-lg mb-2" style={{ color: '#3b82f6', border: '2px solid #3b82f6', borderRadius: '24px' }}>
                  For Property Management Companies
                </div>
              </div>
              
              <div className="space-y-8">
                {/* Step 1 */}
                <div className="flex gap-4">
                  <div className="flex-shrink-0">
                    <div className="h-14 w-14 rounded-full flex items-center justify-center" style={{ backgroundColor: 'rgba(59, 130, 246, 0.1)' }}>
                      <FileText className="h-7 w-7" style={{ color: '#3b82f6' }} />
                    </div>
                  </div>
                  <div>
                    <h3 className="text-xl font-bold mb-2" style={{ color: '#1E1E1E' }}>Create Your Free Profile</h3>
                    <p style={{ color: '#3A3A3A' }}>Tell us about your company, service areas, and specialties.</p>
                  </div>
                </div>

                {/* Step 2 */}
                <div className="flex gap-4">
                  <div className="flex-shrink-0">
                    <div className="h-14 w-14 rounded-full flex items-center justify-center" style={{ backgroundColor: 'rgba(244, 162, 97, 0.15)' }}>
                      <Handshake className="h-7 w-7" style={{ color: '#F4A261' }} />
                    </div>
                  </div>
                  <div>
                    <h3 className="text-xl font-bold mb-2" style={{ color: '#1E1E1E' }}>Receive Qualified Leads</h3>
                    <p style={{ color: '#3A3A3A' }}>Get matched with owners actively looking for management help in your area.</p>
                  </div>
                </div>

                {/* Step 3 */}
                <div className="flex gap-4">
                  <div className="flex-shrink-0">
                    <div className="h-14 w-14 rounded-full flex items-center justify-center" style={{ backgroundColor: 'rgba(244, 162, 97, 0.15)' }}>
                      <BarChart3 className="h-7 w-7" style={{ color: '#F4A261' }} />
                    </div>
                  </div>
                  <div>
                    <h3 className="text-xl font-bold mb-2" style={{ color: '#1E1E1E' }}>Grow Your Portfolio</h3>
                    <p style={{ color: '#3A3A3A' }}>Turn high-intent leads into long-term clients — we make it simple and scalable.</p>
                  </div>
                </div>
              </div>

              <div className="mt-8 text-center">
                <Button
                  onClick={() => navigate("/company-signup")}
                  variant="outline"
                  className="bg-white px-8 py-4 text-base font-semibold transition-all duration-300"
                  style={{ 
                    color: '#3b82f6',
                    borderColor: '#3b82f6',
                    borderWidth: '2px',
                    borderRadius: '12px',
                    boxShadow: '0 4px 12px rgba(0, 0, 0, 0.08)'
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.backgroundColor = '#3b82f6';
                    e.currentTarget.style.color = '#FFFFFF';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.backgroundColor = '#FFFFFF';
                    e.currentTarget.style.color = '#3b82f6';
                  }}
                >
                  Join the Network <ArrowRight className="ml-2 h-5 w-5" />
                </Button>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Benefits Section */}
      <section className="py-20 bg-slate-100">
        <div className="container mx-auto px-4 sm:px-6">
          <div className="text-center mb-16">
            <h2 className="text-3xl sm:text-4xl font-bold mb-4" style={{ color: '#1E1E1E' }}>
              Why Choose TopProp?
            </h2>
            <p className="text-lg" style={{ color: '#3A3A3A' }}>The trusted way to connect property owners and management professionals</p>
          </div>

          <div className="grid md:grid-cols-3 gap-8 lg:gap-12">
            <div className="bg-white p-8 text-center space-y-4 transition-all duration-300" style={{ borderRadius: '12px', boxShadow: '0 4px 12px rgba(0, 0, 0, 0.08)', border: '1px solid #F7F7F7' }}>
              <div className="h-16 w-16 rounded-full flex items-center justify-center mx-auto" style={{ backgroundColor: 'rgba(59, 130, 246, 0.1)' }}>
                <CheckCircle2 className="h-8 w-8" style={{ color: '#3b82f6' }} />
              </div>
              <h3 className="text-xl font-bold" style={{ color: '#1E1E1E' }}>Verified Property Management Partners</h3>
              <p style={{ color: '#3A3A3A' }}>We carefully screen and verify every management company on TopProp — so you only connect with trusted professionals who meet our quality standards.</p>
            </div>

            <div className="bg-white p-8 text-center space-y-4 transition-all duration-300" style={{ borderRadius: '12px', boxShadow: '0 4px 12px rgba(0, 0, 0, 0.08)', border: '1px solid #F7F7F7' }}>
              <div className="h-16 w-16 rounded-full flex items-center justify-center mx-auto" style={{ backgroundColor: 'rgba(244, 162, 97, 0.15)' }}>
                <MapPin className="h-8 w-8" style={{ color: '#F4A261' }} />
              </div>
              <h3 className="text-xl font-bold" style={{ color: '#1E1E1E' }}>Local Expertise, Real Results</h3>
              <p style={{ color: '#3A3A3A' }}>We match you with companies who actually manage properties in your area, ensuring you get local market insight and on-the-ground support.</p>
            </div>

            <div className="bg-white p-8 text-center space-y-4 transition-all duration-300" style={{ borderRadius: '12px', boxShadow: '0 4px 12px rgba(0, 0, 0, 0.08)', border: '1px solid #F7F7F7' }}>
              <div className="h-16 w-16 rounded-full flex items-center justify-center mx-auto" style={{ backgroundColor: 'rgba(59, 130, 246, 0.1)' }}>
                <Zap className="h-8 w-8" style={{ color: '#3b82f6' }} />
              </div>
              <h3 className="text-xl font-bold" style={{ color: '#1E1E1E' }}>Fast, Simple, and Free to Use</h3>
              <p style={{ color: '#3A3A3A' }}>No long forms or waiting games — just a few quick details and you'll be connected to the right management partner in minutes.</p>
            </div>
          </div>
        </div>
      </section>


      {/* What's Your Rent Worth Section */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-4 sm:px-6">
          <div className="max-w-4xl mx-auto">
            {/* Gold Border Card */}
            <div className="border-4 border-yellow-400 rounded-2xl p-8 sm:p-12 bg-gradient-to-br from-yellow-50 to-amber-50 shadow-2xl relative overflow-hidden">
              {/* Gold Accent Bar */}
              <div className="absolute top-0 left-0 right-0 h-2 bg-gradient-to-r from-yellow-400 to-yellow-600"></div>
              
              <div className="text-center relative">
                <h2 className="text-4xl sm:text-5xl font-bold mb-6 text-slate-900" style={{ letterSpacing: '-1px' }}>
                  What's Your Rent Worth?<sup className="text-2xl">®</sup>
                </h2>
                <p className="text-xl sm:text-2xl text-slate-700 mb-6 font-semibold">
                  Curious about your property's rental value?
                </p>
                <p className="text-lg text-slate-600 mb-8 max-w-2xl mx-auto">
                  Get a free, professional rental market analysis from top property management companies in your area. No obligation, just valuable insights.
                </p>
                <div className="flex flex-col sm:flex-row gap-4 justify-center items-center">
                  <Button
                    onClick={() => navigate("/whats-your-rent-worth")}
                    size="lg"
                    className="bg-gradient-to-r from-yellow-400 to-yellow-600 hover:from-yellow-500 hover:to-yellow-700 text-white font-semibold text-lg px-8 py-6 shadow-xl"
                  >
                    Get My Free Rental Analysis
                  </Button>
                  <p className="text-sm text-slate-600 font-medium">
                    ✓ Free Analysis  ✓ No Obligation  ✓ Fast Results
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Lead Form Section */}
      <section id="lead-form" className="py-20">
        <div className="container mx-auto px-4 sm:px-6">
          <div className="max-w-2xl mx-auto">
            <Card className="bg-slate-50 border-slate-300 shadow-2xl">
              <CardContent className="p-8">
                <div>
                  <div className="text-center mb-8">
                    <h2 className="text-3xl font-bold text-slate-900 mb-4">
                      Connect with Top Property Management Companies Now
                    </h2>
                    <p className="text-slate-600">Tell us about your property and we'll connect you with top management companies in your area</p>
                  </div>

                    <form onSubmit={handleSubmit} className="space-y-6" data-testid="lead-form">
                      <div className="grid md:grid-cols-2 gap-6">
                        <div className="space-y-2">
                          <Label htmlFor="name" className="text-slate-700">Full Name *</Label>
                          <Input
                            id="name"
                            data-testid="name-input"
                            required
                            value={formData.name}
                            onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                            placeholder="John Doe"
                            className="border-slate-300"
                          />
                        </div>

                        <div className="space-y-2">
                          <Label htmlFor="email" className="text-slate-700">Email *</Label>
                          <Input
                            id="email"
                            data-testid="email-input"
                            type="email"
                            required
                            value={formData.email}
                            onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                            placeholder="john@example.com"
                            className="border-slate-300"
                          />
                        </div>
                      </div>

                      <div className="space-y-2">
                        <Label htmlFor="phone" className="text-slate-700">Phone Number (Cell Phone Required) *</Label>
                        <Input
                          id="phone"
                          data-testid="phone-input"
                          type="tel"
                          required
                          value={formData.phone}
                          onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                          placeholder="(555) 123-4567"
                          className="border-slate-300"
                        />
                        <p className="text-xs text-amber-600 font-medium">📱 Must be a cell phone that can receive text messages with your matches</p>
                      </div>

                      <div className="space-y-2">
                        <Label htmlFor="property_address" className="text-slate-700">Property Address *</Label>
                        <Input
                          id="property_address"
                          data-testid="property-address-input"
                          required
                          value={formData.property_address}
                          onChange={(e) => setFormData({ ...formData, property_address: e.target.value })}
                          placeholder="123 Main St"
                          className="border-slate-300"
                        />
                      </div>

                      <div className="grid md:grid-cols-2 gap-6">
                        <div className="space-y-2">
                          <Label htmlFor="city" className="text-slate-700">City *</Label>
                          <Input
                            id="city"
                            data-testid="city-input"
                            required
                            value={formData.city}
                            onChange={(e) => setFormData({ ...formData, city: e.target.value })}
                            placeholder="e.g., Vancouver, Toronto, Montreal"
                            className="border-slate-300"
                          />
                        </div>

                        <div className="space-y-2">
                          <Label htmlFor="postal_code" className="text-slate-700">Postal Code *</Label>
                          <Input
                            id="postal_code"
                            data-testid="postal-code-input"
                            required
                            value={formData.postal_code}
                            onChange={(e) => setFormData({ ...formData, postal_code: e.target.value.toUpperCase() })}
                            placeholder="e.g., V6B 3K9, M5V 2T6, H2Y 1C6"
                            maxLength={7}
                            className="border-slate-300"
                          />
                        </div>
                      </div>

                      <div className="grid md:grid-cols-2 gap-6">
                        <div className="space-y-2">
                          <Label htmlFor="property_type" className="text-slate-700">Property Type *</Label>
                          <Select
                            required
                            value={formData.property_type}
                            onValueChange={(value) => setFormData({ ...formData, property_type: value })}
                          >
                            <SelectTrigger data-testid="property-type-select" className="border-slate-300">
                              <SelectValue placeholder="Select type" />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="residential">Residential Property</SelectItem>
                              <SelectItem value="condo">Condo / Strata</SelectItem>
                              <SelectItem value="commercial">Commercial Property</SelectItem>
                              <SelectItem value="multi-family">Multi-Family Property</SelectItem>
                            </SelectContent>
                          </Select>
                        </div>

                        <div className="space-y-2">
                          <Label htmlFor="num_units" className="text-slate-700">Number of Units *</Label>
                          <Input
                            id="num_units"
                            data-testid="num-units-input"
                            type="number"
                            required
                            min="1"
                            value={formData.num_units}
                            onChange={(e) => setFormData({ ...formData, num_units: e.target.value })}
                            placeholder="1"
                            className="border-slate-300"
                          />
                        </div>
                      </div>

                      <div className="space-y-2">
                        <Label htmlFor="management_status" className="text-slate-700">Current Management Status *</Label>
                        <Select
                          required
                          value={formData.management_status}
                          onValueChange={(value) => setFormData({ ...formData, management_status: value })}
                        >
                          <SelectTrigger data-testid="management-status-select" className="border-slate-300">
                            <SelectValue placeholder="Select status" />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="self-managed">Self-Managed</SelectItem>
                            <SelectItem value="currently-managed">Currently Managed</SelectItem>
                            <SelectItem value="looking-to-switch">Looking to Switch</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>

                      <Button
                        type="submit"
                        className="w-full bg-blue-600 hover:bg-blue-700 text-white py-6 text-lg rounded-xl font-semibold shadow-lg shadow-blue-600/30"
                        disabled={submitting}
                        data-testid="submit-button"
                      >
                        {submitting ? "Submitting..." : "Submit"}
                      </Button>
                    </form>
                  </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="text-white py-12" style={{ backgroundColor: '#1E1E1E' }}>
        <div className="container mx-auto px-4 sm:px-6">
          <div className="flex flex-col items-center gap-6">
            <div className="flex items-center gap-2">
              <Building2 className="h-6 w-6" style={{ color: '#3b82f6' }} />
              <span className="text-xl font-bold">Top Prop</span>
            </div>
            <div className="flex flex-wrap items-center justify-center gap-4 text-sm">
              <button
                onClick={() => navigate("/reviews")}
                className="transition-colors duration-300"
                style={{ color: '#9C9C9C' }}
                onMouseEnter={(e) => e.currentTarget.style.color = '#3b82f6'}
                onMouseLeave={(e) => e.currentTarget.style.color = '#9C9C9C'}
              >
                Reviews
              </button>
              <span style={{ color: '#3A3A3A' }}>|</span>
              <button
                onClick={() => navigate("/blog")}
                className="transition-colors duration-300"
                style={{ color: '#9C9C9C' }}
                onMouseEnter={(e) => e.currentTarget.style.color = '#3b82f6'}
                onMouseLeave={(e) => e.currentTarget.style.color = '#9C9C9C'}
              >
                Blog
              </button>
              <span style={{ color: '#3A3A3A' }}>|</span>
              <button
                onClick={() => navigate("/contact")}
                className="transition-colors duration-300"
                style={{ color: '#9C9C9C' }}
                onMouseEnter={(e) => e.currentTarget.style.color = '#3b82f6'}
                onMouseLeave={(e) => e.currentTarget.style.color = '#9C9C9C'}
              >
                Contact Us
              </button>
              <span style={{ color: '#3A3A3A' }}>|</span>
              <button
                onClick={() => navigate("/company-signup")}
                className="transition-colors duration-300"
                style={{ color: '#9C9C9C' }}
                onMouseEnter={(e) => e.currentTarget.style.color = '#3b82f6'}
                onMouseLeave={(e) => e.currentTarget.style.color = '#9C9C9C'}
              >
                Join as Property Manager
              </button>
              <span style={{ color: '#3A3A3A' }}>|</span>
              <button
                onClick={() => navigate("/whats-your-rent-worth")}
                className="transition-colors duration-300"
                style={{ color: '#9C9C9C' }}
                onMouseEnter={(e) => e.currentTarget.style.color = '#F59E0B'}
                onMouseLeave={(e) => e.currentTarget.style.color = '#9C9C9C'}
              >
                What's Your Rent Worth?®
              </button>
            </div>
            <p className="text-sm" style={{ color: '#9C9C9C' }}>© 2025 Top Prop. All rights reserved.</p>
            
            {/* Admin Login Link */}
            <div className="mt-4">
              <button
                onClick={() => navigate("/admin-login")}
                className="text-xs underline transition-colors duration-300"
                style={{ color: '#9C9C9C' }}
                onMouseEnter={(e) => e.currentTarget.style.color = '#F4A261'}
                onMouseLeave={(e) => e.currentTarget.style.color = '#9C9C9C'}
              >
                Admin Login
              </button>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
}